import { getEngagementsBaseUrl } from "src/config/Endpoints";
import i18n from "src/i18n/config";
import { getApiLangFromI18n, normalizeApiLang } from "src/utils/apiLang";
import { getTenantId } from "src/utils/getters";
import { buildAuthHeaders, handleJsonResponse } from "./HttpUtils";

export type SegmentJoin = "and" | "or";

interface SegmentConditionBase {
  /** How this condition joins the one before it; left out on the first condition. */
  join?: SegmentJoin;
}

export interface ListMembershipCondition extends SegmentConditionBase {
  type: "list_membership";
  list_name: string;
  operator: "in" | "not_in";
}

export interface LocationSegmentCondition extends SegmentConditionBase {
  type: "location";
  field: "city" | "province";
  operator: "is_one_of" | "is_not_one_of";
  values: string[];
}

export interface ShoppingActivityCondition extends SegmentConditionBase {
  type: "shopping_activity";
  action: "placed_order";
  comparator: "at_least" | "at_most" | "exactly";
  count: number;
  window_days: number;
}

export interface SignupDateSegmentCondition extends SegmentConditionBase {
  type: "signup_date";
  operator: "before" | "after" | "between";
  date: string;
  date_to?: string;
}

export type SegmentConditionPayload =
  | ListMembershipCondition
  | LocationSegmentCondition
  | ShoppingActivityCondition
  | SignupDateSegmentCondition;

interface ChannelReach {
  count: number;
  countFormattedValue?: string;
  percentage: number;
  percentageFormattedValue?: string;
}

export interface SegmentPreviewResponse {
  /** Matching customers per condition, `index` 1-based in request order. */
  steps: { index: number; count: number }[];
  total: number;
  totalFormattedValue?: string;
  email: ChannelReach;
  whatsapp: ChannelReach;
}

export interface CreateSegmentPayload {
  segment_name: string;
  description?: string;
  tags?: string[];
  conditions: SegmentConditionPayload[];
}

export interface CreateSegmentResponse {
  status: string;
  message: string;
  segment_id: number;
  contact_count: number;
}

const postSegment = async <TResponse,>(
  path: string,
  body: Record<string, unknown>,
  signal?: AbortSignal
): Promise<TResponse> => {
  const params = new URLSearchParams({ lang: normalizeApiLang(getApiLangFromI18n(i18n)) });
  const response = await fetch(`${getEngagementsBaseUrl()}/${path}?${params.toString()}`, {
    method: "POST",
    headers: buildAuthHeaders("api_key"),
    body: JSON.stringify({ tenant_id: Number(getTenantId()), ...body }),
    signal,
  });
  return handleJsonResponse<TResponse>(response);
};

/** Live count while the user builds conditions; nothing is saved. */
export const previewSegment = (conditions: SegmentConditionPayload[], signal?: AbortSignal) =>
  postSegment<SegmentPreviewResponse>("audience/preview-segment", { conditions }, signal);

export const createSegment = (payload: CreateSegmentPayload) =>
  postSegment<CreateSegmentResponse>("create-segment", { ...payload });
